import { hashToCurve } from '../crypto';
import { safeCallback } from '../logger';
import { CTSError } from '../model/Errors';
import { MintQuoteState, MeltQuoteState } from '../model/types';
import type {
  Proof,
  ProofLike,
  ProofState,
  MeltQuoteBolt11Response,
  MintQuoteBolt11Response,
} from '../model/types';

import { type OperationCounters } from './CounterSource';
import type { Wallet } from './Wallet';

export type SubscriptionCanceller = () => void;

export type CancellerLike = SubscriptionCanceller | Promise<SubscriptionCanceller>;

export type SubscribeOpts = { signal?: AbortSignal };

function safeStringify(obj: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(obj, (_k: string, v: unknown) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v; // returning `unknown` is fine
    });
  } catch {
    return Object.prototype.toString.call(obj);
  }
}

function normalizeError(err: unknown): Error {
  if (err instanceof Error) return err;
  const message = typeof err === 'string' ? err : safeStringify(err);
  return new CTSError(message, { cause: err });
}

function makeAbortError(): Error {
  const e = new Error('Aborted');
  Object.defineProperty(e, 'name', { value: 'AbortError' });
  return e;
}

function cancelSafely(c: CancellerLike | null | undefined): void {
  if (!c) return;
  void Promise.resolve(c)
    .then((fn) => {
      try {
        fn();
      } catch {
        /* ignore canceller errors */
      }
      return;
    })
    .catch(() => {
      /* ignore awaiting-canceller errors */
    });
}

export class WalletEvents {
  constructor(private wallet: Wallet) {}

  // Callbacks registered for Counters Reserved events
  private countersReservedHandlers = new Set<(payload: OperationCounters) => void>();

  // Binds an abort signal to each subscription canceller
  private withAbort(
    signal: AbortSignal | undefined,
    cancel: SubscriptionCanceller,
  ): SubscriptionCanceller {
    if (!signal) return cancel;
    if (signal.aborted) {
      cancel();
      return () => {
        /* noop */
      };
    }
    const onAbort = () => cancel();
    signal.addEventListener('abort', onAbort, { once: true });
    return () => {
      signal.removeEventListener('abort', onAbort);
      cancel();
    };
  }

  // Subscribe to a quote-paid event and resolve when it fires.
  // Supports AbortSignal and timeout, and always cleans up.
  private waitUntilPaid<T>(
    subscribeFn: (
      id: string,
      cb: (p: T) => void, // called when the entity becomes PAID
      err: (e: Error) => void, // called if the subscription itself errors
      opts?: { signal?: AbortSignal },
    ) => Promise<SubscriptionCanceller>,
    id: string, // identifier of the mint/melt/etc. to watch
    opts?: SubscribeOpts & { timeoutMs?: number },
    timeoutMsg = 'Timeout waiting for paid',
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      let cancelP: Promise<SubscriptionCanceller> | null = null; // handle to unsub later
      let to: ReturnType<typeof setTimeout> | null = null; // optional timeout timer
      let done = false;

      // Common cleanup: cancels subscription, clears timer, detaches abort listener.
      // If an error is provided, rejects the promise with it.
      const cleanup = (err?: unknown) => {
        if (done) return;
        done = true;
        cancelSafely(cancelP);
        if (to) {
          clearTimeout(to);
          to = null;
        }
        if (opts?.signal) opts.signal.removeEventListener('abort', onAbort);
        if (err) reject(normalizeError(err));
      };

      // Abort handler produces a standardized AbortError and rejects.
      const onAbort = () => cleanup(makeAbortError());

      // Hook up AbortSignal if provided.
      if (opts?.signal) {
        if (opts.signal.aborted) return onAbort(); // already aborted
        opts.signal.addEventListener('abort', onAbort, { once: true });
      }

      // Start a timeout if requested.
      if (opts?.timeoutMs && opts.timeoutMs > 0) {
        to = setTimeout(() => cleanup(new CTSError(timeoutMsg)), opts.timeoutMs);
      }

      // Subscribe to the actual event. Canceller returned is saved to cancelP.
      cancelP = subscribeFn(
        id,
        (p) => {
          cleanup(); // clean up resources
          resolve(p); // resolve promise with payload
        },
        (e) => cleanup(e), // reject if subscription itself errors
        { signal: opts?.signal }, // delegate abort to subscription as well
      );

      // catch errors starting the subscription
      void cancelP.catch((e) => cleanup(e));
    });
  }

  /**
   * Register a callback that fires whenever deterministic counters are reserved.
   *
   * Timing: the callback is invoked synchronously _after_ a successful reservation and _before_ the
   * enclosing wallet method returns. The wallet does **not** await your callback, it is
   * fire-and-forget.
   *
   * Responsibility for async work is on the consumer. If your handler calls an async function (e.g.
   * persisting `start + count` to storage), make sure to handle errors inside it to avoid unhandled
   * rejections.
   *
   * Typical use: persist `start + count` for the `keysetId` so counters survive restarts.
   *
   * @example
   *
   * ```ts
   * wallet.on.countersReserved(({ keysetId, start, count, next }) => {
   *   saveNextToDb(keysetId, start + count); // handle async errors inside saveNextToDb
   * });
   * ```
   *
   * @param cb Handler called with { keysetId, start, count }.
   * @returns A function that unsubscribes the handler.
   */
  public countersReserved(
    cb: (payload: OperationCounters) => void,
    opts?: SubscribeOpts,
  ): SubscriptionCanceller {
    this.countersReservedHandlers.add(cb);
    const cancel = () => this.countersReservedHandlers.delete(cb);
    return this.withAbort(opts?.signal, cancel);
  }
  /**
   * @internal
   */
  public _emitCountersReserved(payload: OperationCounters) {
    for (const h of this.countersReservedHandlers) {
      safeCallback(h, payload, this.wallet.logger, { event: 'countersReserved' });
    }
  }

  /**
   * Register a callback to be called whenever a mint quote's state changes.
   *
   * @param quoteIds List of mint quote IDs that should be subscribed to.
   * @param callback Callback function that will be called whenever a mint quote state changes.
   * @param errorCallback
   * @returns
   */
  async mintQuoteUpdates(
    ids: string[],
    cb: (p: MintQuoteBolt11Response) => void,
    err: (e: Error) => void,
    opts?: SubscribeOpts,
  ): Promise<SubscriptionCanceller> {
    await this.wallet.mint.connectWebSocket();
    const ws = this.wallet.mint.webSocketConnection;
    if (!ws) throw new CTSError('Failed to establish WebSocket connection.');

    const uniq = Array.from(new Set(ids));
    const subId = ws.createSubscription({ kind: 'bolt11_mint_quote', filters: uniq }, cb, err);
    const cancel = () => ws.cancelSubscription(subId, cb);
    return this.withAbort(opts?.signal, cancel);
  }

  /**
   * Register a callback to be called when a single mint quote gets paid.
   *
   * @param quoteId Mint quote id that should be subscribed to.
   * @param callback Callback function that will be called when this mint quote gets paid.
   * @param errorCallback
   * @returns
   */
  async mintQuotePaid(
    id: string,
    cb: (p: MintQuoteBolt11Response) => void,
    err: (e: Error) => void,
    opts?: SubscribeOpts,
  ): Promise<SubscriptionCanceller> {
    return this.mintQuoteUpdates(
      [id],
      (p) => {
        if (p.state === MintQuoteState.PAID) cb(p);
      },
      err,
      opts,
    );
  }

  /**
   * Register a callback to be called whenever a melt quote’s state changes.
   *
   * @param quoteId Melt quote id that should be subscribed to.
   * @param callback Callback function that will be called when this melt quote gets paid.
   * @param errorCallback
   * @returns
   */
  async meltQuoteUpdates(
    ids: string[],
    cb: (p: MeltQuoteBolt11Response) => void,
    err: (e: Error) => void,
    opts?: SubscribeOpts,
  ): Promise<SubscriptionCanceller> {
    await this.wallet.mint.connectWebSocket();
    const ws = this.wallet.mint.webSocketConnection;
    if (!ws) throw new CTSError('Failed to establish WebSocket connection.');

    const uniq = Array.from(new Set(ids));
    const subId = ws.createSubscription({ kind: 'bolt11_melt_quote', filters: uniq }, cb, err);
    const cancel = () => ws.cancelSubscription(subId, cb);
    return this.withAbort(opts?.signal, cancel);
  }

  /**
   * Register a callback to be called when a single melt quote gets paid.
   *
   * @param quoteIds List of melt quote IDs that should be subscribed to.
   * @param callback Callback function that will be called whenever a melt quote state changes.
   * @param errorCallback
   * @returns
   */
  async meltQuotePaid(
    id: string,
    cb: (p: MeltQuoteBolt11Response) => void,
    err: (e: Error) => void,
    opts?: SubscribeOpts,
  ): Promise<SubscriptionCanceller> {
    return this.meltQuoteUpdates(
      [id],
      (p) => {
        if (p.state === MeltQuoteState.PAID) cb(p);
      },
      err,
      opts,
    );
  }

  /**
   * Register a callback to be called whenever a subscribed proof state changes.
   *
   * Only `secret` is read from each proof to derive the subscription filter; any `ProofLike`-shaped
   * object (e.g. proofs loaded from storage where `amount` has not yet been normalized to `Amount`)
   * may be passed without conversion. The original proof object is echoed back on the callback
   * payload as the inferred input type.
   *
   * @param proofs List of proofs that should be subscribed to.
   * @param callback Callback function that will be called whenever a proof's state changes.
   * @param errorCallback
   * @returns
   */
  async proofStateUpdates<T extends ProofLike = Proof>(
    proofs: T[],
    cb: (payload: ProofState & { proof: T }) => void,
    err: (e: Error) => void,
    opts?: SubscribeOpts,
  ): Promise<SubscriptionCanceller> {
    await this.wallet.mint.connectWebSocket();
    const ws = this.wallet.mint.webSocketConnection;
    if (!ws) throw new CTSError('Failed to establish WebSocket connection.');

    const enc = new TextEncoder();
    // Object.create(null) avoids prototype-key collisions: a mint sending
    // payload.Y === '__proto__' (or 'constructor', etc.) would otherwise
    // resolve to an inherited property and bypass the unknown-Y guard below.
    const proofMap = Object.create(null) as Record<string, T>;
    for (const p of proofs) {
      const y = hashToCurve(enc.encode(p.secret)).toHex(true);
      if (proofMap[y]) {
        throw new CTSError('Duplicate proof secret in proofStateUpdates input');
      }
      proofMap[y] = p;
    }
    const ys = Object.keys(proofMap);

    const handler = (payload: ProofState) => {
      const proof = proofMap[payload.Y];
      if (!proof) return; // ignore unsolicited Y from a misbehaving mint
      cb({ ...payload, proof });
    };
    const subId = ws.createSubscription({ kind: 'proof_state', filters: ys }, handler, err);
    const cancel = () => ws.cancelSubscription(subId, handler);

    return this.withAbort(opts?.signal, cancel);
  }

  /**
   * Resolve once a mint quote transitions to PAID, with automatic unsubscription, optional abort
   * signal, and optional timeout.
   *
   * The underlying subscription is always cancelled after resolution or rejection, including on
   * timeout or abort.
   *
   * @example
   *
   * ```ts
   * const ac = new AbortController();
   * // Cancel if the user navigates away
   * window.addEventListener('beforeunload', () => ac.abort(), { once: true });
   *
   * try {
   *   const paid = await wallet.on.onceMintPaid(quoteId, {
   *     signal: ac.signal,
   *     timeoutMs: 60_000,
   *   });
   *   console.log('Mint paid, amount', paid.amount);
   * } catch (e) {
   *   if ((e as Error).name === 'AbortError') {
   *     console.log('User aborted');
   *   } else {
   *     console.error('Mint not paid', e);
   *   }
   * }
   * ```
   *
   * @param id Mint quote id to watch.
   * @param opts Optional controls.
   * @param opts.signal AbortSignal to cancel the wait early.
   * @param opts.timeoutMs Milliseconds to wait before rejecting with a timeout error.
   * @returns A promise that resolves with the latest `MintQuoteBolt11Response` once PAID.
   */
  onceMintPaid(
    id: string,
    opts?: { signal?: AbortSignal; timeoutMs?: number },
  ): Promise<MintQuoteBolt11Response> {
    return this.waitUntilPaid<MintQuoteBolt11Response>(
      this.mintQuotePaid.bind(this),
      id,
      opts,
      'Timeout waiting for mint paid',
    );
  }

  /**
   * Resolve when ANY of several mint quotes is PAID, cancelling the rest.
   *
   * Subscribes to all distinct ids, resolves with `{ id, quote }` for the first PAID, and cancels
   * all remaining subscriptions.
   *
   * Errors from individual subscriptions are ignored by default so a single noisy stream does not
   * abort the whole race. Set `failOnError: true` to reject on the first error instead. If all
   * subscriptions error and none paid, the promise rejects with the last seen error.
   *
   * @example
   *
   * ```ts
   * // Race multiple quotes obtained from splitting a large top up
   * const { id, quote } = await wallet.on.onceAnyMintPaid(batchQuoteIds, {
   *   timeoutMs: 120_000,
   * });
   * console.log('First top up paid', id, quote.preimage?.length);
   * ```
   *
   * @param ids Array of mint quote ids (duplicates are ignored).
   * @param opts Optional controls.
   * @param opts.signal AbortSignal to cancel the wait early.
   * @param opts.timeoutMs Milliseconds to wait before rejecting with a timeout error.
   * @param opts.failOnError When true, reject on first error. Default false.
   * @returns A promise resolving to the id that won and its `MintQuoteBolt11Response`.
   */
  onceAnyMintPaid(
    ids: string[],
    opts?: { signal?: AbortSignal; timeoutMs?: number; failOnError?: boolean },
  ): Promise<{ id: string; quote: MintQuoteBolt11Response }> {
    return new Promise((resolve, reject) => {
      const unique = Array.from(new Set(ids));
      const cancels: Map<string, CancellerLike> = new Map();
      let to: ReturnType<typeof setTimeout> | null = null;
      let lastError: unknown = null;
      let fullyRegistered = false;
      let done = false;

      const cleanup = (err?: unknown) => {
        if (done) return;
        done = true;
        for (const c of cancels.values()) {
          cancelSafely(c);
        }
        cancels.clear();
        if (to) {
          clearTimeout(to);
          to = null;
        }
        if (opts?.signal) opts.signal.removeEventListener('abort', onAbort);
        if (err) reject(normalizeError(err));
      };

      const onAbort = () => cleanup(makeAbortError());

      if (opts?.signal) {
        if (opts.signal.aborted) return onAbort();
        opts.signal.addEventListener('abort', onAbort, { once: true });
      }

      if (opts?.timeoutMs && opts.timeoutMs > 0) {
        to = setTimeout(
          () => cleanup(new CTSError('Timeout waiting for any mint paid')),
          opts.timeoutMs,
        );
      }

      if (unique.length === 0) return cleanup(new CTSError('No quote ids provided'));

      for (const quoteId of unique) {
        const c = this.mintQuotePaid(
          quoteId,
          (p) => {
            cleanup();
            resolve({ id: quoteId, quote: p });
          },
          (e) => {
            // Catch errors after setup
            if (opts?.failOnError) {
              cleanup(e);
              return;
            }
            lastError = e;

            const thisCanceller = cancels.get(quoteId);
            if (thisCanceller) {
              cancelSafely(thisCanceller);
              cancels.delete(quoteId);
            }

            if (fullyRegistered && cancels.size === 0) {
              cleanup(lastError ?? new CTSError('No subscriptions remaining'));
            }
          },
        );

        cancels.set(quoteId, c);

        // Catch errors setting up
        void c.catch((e) => {
          if (opts?.failOnError) {
            cleanup(e);
            return;
          }
          lastError = e;

          const thisCanceller = cancels.get(quoteId);
          if (thisCanceller) {
            cancelSafely(thisCanceller);
            cancels.delete(quoteId);
          }

          if (fullyRegistered && cancels.size === 0) {
            cleanup(lastError ?? new CTSError('No subscriptions remaining'));
          }
        });
      }

      fullyRegistered = true;
    });
  }

  /**
   * Resolve once a melt quote transitions to PAID, with automatic unsubscription, optional abort
   * signal, and optional timeout.
   *
   * Mirrors onceMintPaid, but for melts.
   *
   * @example
   *
   * ```ts
   * try {
   *   const paid = await wallet.on.onceMeltPaid(meltId, { timeoutMs: 45_000 });
   *   console.log('Invoice paid by mint, paid msat', paid.paid ?? 0);
   * } catch (e) {
   *   console.error('Payment did not complete in time', e);
   * }
   * ```
   *
   * @param id Melt quote id to watch.
   * @param opts Optional controls.
   * @param opts.signal AbortSignal to cancel the wait early.
   * @param opts.timeoutMs Milliseconds to wait before rejecting with a timeout error.
   * @returns A promise that resolves with the `MeltQuoteBolt11Response` once PAID.
   */
  onceMeltPaid(
    id: string,
    opts?: { signal?: AbortSignal; timeoutMs?: number },
  ): Promise<MeltQuoteBolt11Response> {
    return this.waitUntilPaid<MeltQuoteBolt11Response>(
      this.meltQuotePaid.bind(this),
      id,
      opts,
      'Timeout waiting for melt paid',
    );
  }

  /**
   * Async iterable that yields proof state updates for the provided proofs.
   *
   * @remarks
   * Adds a bounded buffer option:
   *
   * - If `maxBuffer` is set and the queue is full when a new payload arrives, either drop the oldest
   *   queued payload (`drop: 'oldest'`, default) or the incoming payload (`drop: 'newest'`). In
   *   both cases `onDrop` is invoked with the dropped payload.
   *
   * The stream ends and cleans up on abort or on the wallet error callback. Errors from the wallet
   * are treated as a graceful end for this iterator.
   *
   * The subscription is sent to the mint on the first iteration, not when this method is called.
   * Per NUT-17 the mint replays the current state on subscribe, so the latest state is never lost;
   * only intermediate transitions before the first iteration are collapsed into that snapshot.
   * @example
   *
   * ```ts
   * const ac = new AbortController();
   * try {
   *   for await (const update of wallet.on.proofStatesStream(myProofs)) {
   *     if (update.state === CheckStateEnum.SPENT) {
   *       console.warn('Spent proof', update.proof.id);
   *     }
   *   }
   * } catch (e) {
   *   if ((e as Error).name !== 'AbortError') {
   *     console.error('Stream error', e);
   *   }
   * }
   * ```
   *
   * @param proofs The proofs to subscribe to. Only `secret` is required, so any `ProofLike`-shaped
   *   object may be passed without first normalizing `amount` to `Amount`.
   * @param opts Optional controls.
   * @param opts.signal AbortSignal that stops the stream when aborted.
   * @param opts.maxBuffer Maximum number of queued items before applying the drop strategy.
   * @param opts.drop Overflow strategy when `maxBuffer` is reached, 'oldest' | 'newest'. Default
   *   'oldest'.
   * @param opts.onDrop Callback invoked with the payload that was dropped.
   * @returns An async iterable of update payloads. The `proof` field on each payload preserves the
   *   input proof type.
   */
  proofStatesStream<P extends ProofLike = Proof>(
    proofs: P[],
    opts?: {
      signal?: AbortSignal;
      maxBuffer?: number;
      drop?: 'oldest' | 'newest';
      onDrop?: (payload: ProofState & { proof: P }) => void;
    },
  ): AsyncIterable<ProofState & { proof: P }> {
    type Payload = ProofState & { proof: P };
    return async function* (this: WalletEvents) {
      const queue: Payload[] = [];
      let done = false;
      let notify: (() => void) | null = null;

      const max = opts?.maxBuffer && opts.maxBuffer > 0 ? opts.maxBuffer : Infinity;
      const dropMode: 'oldest' | 'newest' = opts?.drop ?? 'oldest';

      const wake = () => {
        const n = notify;
        notify = null;
        if (n) n();
      };

      const push = (payload: Payload) => {
        if (queue.length >= max) {
          if (dropMode === 'oldest') {
            const dropped = queue.shift();
            if (dropped !== undefined) {
              try {
                opts?.onDrop?.(dropped);
              } catch {
                /* noop */
              }
            }
            queue.push(payload);
          } else {
            try {
              opts?.onDrop?.(payload);
            } catch {
              /* noop */
            }
            return; // drop newest
          }
        } else {
          queue.push(payload);
        }
        wake();
      };

      let setupErr: Error | null = null;
      // Subscribing here (inside the generator body, so on first iteration) is
      // safe: NUT-17 requires the mint to replay the current proof state on
      // subscribe, so a state update made before iteration starts is not lost.
      const cancelP: Promise<SubscriptionCanceller> = this.proofStateUpdates<P>(
        proofs,
        push,
        () => {
          done = true;
          wake();
        },
        { signal: opts?.signal },
      );
      // Attach a catch handler in the same tick so a synchronous setup
      // failure (e.g. duplicate proof secrets) cannot escape as an
      // unhandled rejection. The error is surfaced once the loop drains.
      cancelP.catch((e: unknown) => {
        setupErr = normalizeError(e);
        done = true;
        wake();
      });

      const onAbort = () => {
        done = true;
        wake();
      };

      try {
        if (opts?.signal) {
          if (opts.signal.aborted) onAbort();
          else opts.signal.addEventListener('abort', onAbort, { once: true });
        }
        while (!done || queue.length) {
          while (queue.length) yield queue.shift()!;
          if (done) break;
          await new Promise<void>((resolve) => (notify = resolve));
        }
        // Check after the loop, not inside. The catch handler sets done=true
        // before waking the awaited notify, so the next loop iteration exits
        // immediately and an in-loop throw would never be reached.
        if (setupErr) {
          const err: Error = setupErr;
          throw err;
        }
      } finally {
        cancelSafely(cancelP);
        if (opts?.signal) opts.signal.removeEventListener('abort', onAbort);
      }
    }.call(this);
  }

  /**
   * Create a composite canceller that can collect many subscriptions and dispose them all in one
   * call.
   *
   * Accepts both a `SubscriptionCanceller` and a `Promise<SubscriptionCanceller>`. When the
   * composite canceller is called, all collected cancellations are invoked. Errors from individual
   * cancellers are caught and ignored.
   *
   * The returned function also has an `.add()` method to register more cancellers, and a
   * `.cancelled` boolean property for debugging.
   *
   * @example
   *
   * ```ts
   * const cancelAll = wallet.on.group();
   * cancelAll.add(wallet.on.mintQuotes(ids, onUpdate, onErr));
   * cancelAll.add(asyncSubscribeElsewhere());
   *
   * // later
   * cancelAll(); // disposes everything
   * ```
   *
   * @returns Composite canceller function with `.add()` and `.cancelled` members.
   */
  group(): SubscriptionCanceller & {
    add: (c: CancellerLike) => CancellerLike;
    cancelled: boolean;
  } {
    const cancels: CancellerLike[] = [];
    let cancelled = false;

    const cancelAll = (() => {
      if (cancelled) return;
      cancelled = true;
      while (cancels.length) cancelSafely(cancels.pop());
    }) as SubscriptionCanceller & {
      add: (c: CancellerLike) => CancellerLike;
      cancelled: boolean;
    };

    cancelAll.add = (c: CancellerLike) => {
      if (cancelled) {
        // already cancelled, immediately dispose newly added subscription
        cancelSafely(c);
        return c;
      }
      cancels.push(c);
      return c;
    };

    Object.defineProperty(cancelAll, 'cancelled', {
      get: () => cancelled,
      enumerable: true,
    });

    return cancelAll;
  }
}
